import { useEffect, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Alert, Badge, Box, Button, Center, Group, Loader, Modal, SegmentedControl, Stack, Text, Tooltip } from '@mantine/core';
import { useElementSize, useHotkeys, useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconArrowBackUp, IconBlur, IconInfoCircle, IconSquareFilled } from '@tabler/icons-react';
import { clampRegion, redactPixels, type RedactionMode, type RedactionRegion } from '../../lib/redactImage';

/** Obergrenze wie im Backend (customerDeadlineProofRules) */
const MAX_BYTES = 8 * 1024 * 1024;
/** Kürzer gezogen ist eher ein Klick als eine Markierung — gemessen in Bildschirmpixeln. */
const MIN_DRAG_SCREEN_PX = 4;

/** Ein Bild, das vor dem Ablegen bearbeitet werden kann. */
export interface RedactTarget {
  file: File;
  /** Gesetzt, wenn ein bereits eingefügter, noch nicht gespeicherter Beleg nachbearbeitet wird */
  replaceKey?: string;
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Das Bild konnte nicht geladen werden.'));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

function redactedFileName(name: string, extension: string): string {
  const base = name.replace(/\.[^.]+$/, '') || 'beleg';
  return `${base}-verpixelt.${extension}`;
}

/**
 * Beleg vor dem Ablegen unkenntlich machen: Rechtecke aufziehen, verpixeln oder schwärzen.
 *
 * Gezeichnet wird immer vom Original aus, damit „Rückgängig" funktioniert — das Original
 * bleibt aber nur hier im Speicher. Übernommen wird ein neu kodiertes Bild (ohne Metadaten);
 * wer nichts markiert, übernimmt die Datei unverändert.
 */
export function ProofRedactModal({
  target,
  remaining,
  onApply,
  onDiscard,
}: {
  target: RedactTarget | null;
  /** Wie viele Bilder danach noch warten */
  remaining: number;
  onApply: (file: File) => void;
  onDiscard: () => void;
}) {
  const isMobile = useMediaQuery('(max-width: 48em)');
  const { ref: frameRef, width: frameWidth } = useElementSize();
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [regions, setRegions] = useState<RedactionRegion[]>([]);
  const [mode, setMode] = useState<RedactionMode>('pixelate');
  const [draft, setDraft] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setRegions([]);
    setDraft(null);
    setImage(null);
    setLoadError(null);
    if (!target) return;
    let cancelled = false;
    loadImage(target.file)
      .then((img) => !cancelled && setImage(img))
      .catch((err: Error) => !cancelled && setLoadError(err.message));
    return () => {
      cancelled = true;
    };
  }, [target]);

  // Jedes Mal vom Original aus neu zeichnen und alle Bereiche anwenden.
  useEffect(() => {
    if (!canvas || !image) return;
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
    ctx.drawImage(image, 0, 0);
    for (const region of regions) {
      const area = clampRegion(region, canvas.width, canvas.height);
      if (!area) continue;
      const data = ctx.getImageData(area.x, area.y, area.width, area.height);
      redactPixels(data.data, area.width, area.height, { ...area, x: 0, y: 0 });
      ctx.putImageData(data, area.x, area.y);
    }
  }, [canvas, image, regions]);

  const undo = () => setRegions((current) => current.slice(0, -1));
  useHotkeys(target ? [['mod+Z', undo]] : []);

  // Anzeigegröße ausdrücklich rechnen: Mit max-width/max-height allein passte das
  // Auswahl-Rechteck nicht mehr über das Bild, sobald die Höhe begrenzt.
  const scale = image
    ? Math.min(
        1,
        frameWidth > 0 ? frameWidth / image.naturalWidth : 1,
        (window.innerHeight * (isMobile ? 0.65 : 0.6)) / image.naturalHeight
      )
    : 1;
  const displayWidth = image ? Math.round(image.naturalWidth * scale) : 0;
  const displayHeight = image ? Math.round(image.naturalHeight * scale) : 0;

  const pointOf = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * event.currentTarget.width,
      y: ((event.clientY - rect.top) / rect.height) * event.currentTarget.height,
    };
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!image || busy) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const p = pointOf(event);
    setDraft({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!draft) return;
    const p = pointOf(event);
    setDraft({ ...draft, x1: p.x, y1: p.y });
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!draft) return;
    const p = pointOf(event);
    setDraft(null);
    const width = p.x - draft.x0;
    const height = p.y - draft.y0;
    if (Math.abs(width) * scale < MIN_DRAG_SCREEN_PX || Math.abs(height) * scale < MIN_DRAG_SCREEN_PX) return;
    setRegions((current) => [...current, { x: draft.x0, y: draft.y0, width, height, mode }]);
  };

  const apply = async () => {
    if (!target) return;
    if (regions.length === 0) {
      onApply(target.file);
      return;
    }
    if (!canvas) return;
    setBusy(true);
    try {
      // PNG hält Schrift scharf; nur wenn das zu groß wird, JPEG.
      let blob = await canvasToBlob(canvas, 'image/png');
      let extension = 'png';
      if (blob && blob.size > MAX_BYTES) {
        blob = await canvasToBlob(canvas, 'image/jpeg', 0.9);
        extension = 'jpg';
      }
      if (!blob) throw new Error('Das Bild konnte nicht erzeugt werden.');
      if (blob.size > MAX_BYTES) throw new Error('Das Bild ist auch als JPEG noch größer als 8 MB.');
      onApply(new File([blob], redactedFileName(target.file.name, extension), { type: blob.type }));
    } catch (err) {
      notifications.show({
        title: 'Beleg nicht übernommen',
        message: err instanceof Error ? err.message : 'Unbekannter Fehler',
        color: 'red',
      });
    } finally {
      setBusy(false);
    }
  };

  const pct = (value: number, total: number) => `${(value / total) * 100}%`;

  return (
    <Modal
      opened={!!target}
      onClose={onDiscard}
      title={target?.replaceKey ? 'Beleg verpixeln' : 'Beleg vor dem Ablegen verpixeln'}
      size="xl"
      fullScreen={!!isMobile}
      zIndex={1000}
      centered
    >
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Rechteck über Einkaufspreise, Kontodaten oder Namen ziehen. „Verpixeln" macht den Bereich grob und mit
          Rauschen unlesbar, „Schwärzen" deckt ihn vollständig ab. Gespeichert wird nur das Ergebnis — das Original
          verlässt deinen Browser nicht.
        </Text>

        <Group justify="space-between" wrap="wrap" gap="xs">
          <SegmentedControl
            size="xs"
            value={mode}
            onChange={(value) => setMode(value as RedactionMode)}
            data={[
              {
                value: 'pixelate',
                label: (
                  <Group gap={4} wrap="nowrap" justify="center">
                    <IconBlur size={14} />
                    <span>Verpixeln</span>
                  </Group>
                ),
              },
              {
                value: 'blackout',
                label: (
                  <Group gap={4} wrap="nowrap" justify="center">
                    <IconSquareFilled size={14} />
                    <span>Schwärzen</span>
                  </Group>
                ),
              },
            ]}
          />
          <Group gap="xs">
            {regions.length > 0 && (
              <Badge variant="light" color="orange">
                {regions.length} {regions.length === 1 ? 'Bereich' : 'Bereiche'}
              </Badge>
            )}
            <Tooltip label="Letzten Bereich zurücknehmen (⌘Z / Strg+Z)">
              <Button
                size="xs"
                variant="default"
                leftSection={<IconArrowBackUp size={14} />}
                disabled={regions.length === 0 || busy}
                onClick={undo}
              >
                Rückgängig
              </Button>
            </Tooltip>
          </Group>
        </Group>

        <Box
          ref={frameRef}
          style={{
            display: 'flex',
            justifyContent: 'center',
            borderRadius: 'var(--mantine-radius-md)',
            backgroundColor: 'var(--mantine-color-default-hover)',
          }}
        >
          {loadError ? (
            <Alert color="red" variant="light" icon={<IconInfoCircle size={16} />} m="md">
              {loadError}
            </Alert>
          ) : !image ? (
            <Center h={200}>
              <Loader size="sm" />
            </Center>
          ) : (
            <Box style={{ position: 'relative', width: displayWidth, height: displayHeight }}>
              <canvas
                ref={setCanvas}
                aria-label="Beleg — Bereich zum Verpixeln aufziehen"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={() => setDraft(null)}
                style={{
                  display: 'block',
                  width: displayWidth,
                  height: displayHeight,
                  cursor: 'crosshair',
                  touchAction: 'none',
                }}
              />
              {draft && canvas && canvas.width > 0 && (
                <Box
                  style={{
                    position: 'absolute',
                    pointerEvents: 'none',
                    left: pct(Math.min(draft.x0, draft.x1), canvas.width),
                    top: pct(Math.min(draft.y0, draft.y1), canvas.height),
                    width: pct(Math.abs(draft.x1 - draft.x0), canvas.width),
                    height: pct(Math.abs(draft.y1 - draft.y0), canvas.height),
                    border: '2px dashed var(--mantine-color-orange-6)',
                    backgroundColor: mode === 'blackout' ? 'rgba(0, 0, 0, 0.35)' : 'rgba(255, 122, 0, 0.15)',
                  }}
                />
              )}
            </Box>
          )}
        </Box>

        <Group justify="space-between" wrap="wrap" gap="xs">
          <Text size="xs" c="dimmed">
            {remaining > 0 ? `Danach ${remaining === 1 ? 'noch ein Bild' : `noch ${remaining} Bilder`}` : ''}
          </Text>
          <Group gap="xs">
            <Button variant="subtle" color="gray" onClick={onDiscard} disabled={busy}>
              {target?.replaceKey ? 'Abbrechen' : 'Bild verwerfen'}
            </Button>
            <Button onClick={() => void apply()} loading={busy} disabled={!image}>
              {regions.length > 0 ? 'Verpixelt übernehmen' : 'Unverändert übernehmen'}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
